import React from 'react'
import { createPortal } from 'react-dom'
import { VideoCameraIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Text } from '../ui'
import { PsychologicalAttendForm } from './PsychologicalAttendForm'
import { SimpleAttendanceNotesForm } from './SimpleAttendanceNotesForm'
import { AppointmentAttachmentsList } from './AppointmentAttachmentsList'
import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import {
  ASSISTANCE_FORM_KIND_MISSING,
  SUPPORT_CENTER_FORM_KIND_LABELS,
  appointmentFormKind,
} from '../../types/supportCenterSpecialties'
import { attendanceNotesLabel, isReducedTriageForm } from '../../types/supportCenterTriage'
import { formatDateOnlyPtBr } from '../../utils/dateOnly'
import { formatCpf, patientKinshipLabel } from './dashboardHelpers'

type AppointmentAttendModalProps = {
  open: boolean
  appointment: SupportCenterAgendaEvent | null
  canUpdate: boolean
  onClose: () => void
  /** Chamado após envio do formulário (não finaliza o agendamento). */
  onFormSubmitted?: () => void
  /** Chamado após gravar o rascunho, antes de fechar o modal. */
  onDraftSaved?: () => void
}

function meetingLinkOf(appointment: SupportCenterAgendaEvent): string {
  return (appointment.call_link || appointment.call_link_short || '').trim()
}

function GeneralField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" color="muted">
        {label}
      </Text>
      <div className="text-sm font-medium text-gray-900 dark:text-white">{value || '—'}</div>
    </div>
  )
}

/**
 * Formulário de atendimento pela agenda: prontuário psicológico ou texto único
 * (observações / orientação) conforme o form_kind.
 * Não finaliza o agendamento — apenas salva/envia o formulário.
 */
export const AppointmentAttendModal: React.FC<AppointmentAttendModalProps> = ({
  open,
  appointment,
  canUpdate,
  onClose,
  onFormSubmitted,
  onDraftSaved,
}) => {
  const [actionsHost, setActionsHost] = React.useState<HTMLDivElement | null>(null)

  if (!open || !appointment) return null

  const formKind = appointmentFormKind(appointment)
  const isPsychological = formKind === 'psicologico'
  const isNotesForm = isReducedTriageForm(formKind)
  const notesLabel = attendanceNotesLabel(formKind)
  const formLabel = formKind ? SUPPORT_CENTER_FORM_KIND_LABELS[formKind] : null
  const triageOk = appointment.triage_status === 'concluida'
  /** Editável até o formulário ser enviado (submitted_at); finalizar o atendimento não trava.
   * `reagendado` = agendamento antigo; formulário/atendimento só no vigente (`marcado`/`realizado`). */
  const canEditForm =
    canUpdate &&
    triageOk &&
    (appointment.status === 'marcado' || appointment.status === 'realizado')
  const isFallbackForm = Boolean(formKind) && !isPsychological && !isNotesForm
  const meetingLink = meetingLinkOf(appointment)

  const openMeetingLink = () => {
    if (!meetingLink) return
    window.open(meetingLink, '_blank', 'noopener,noreferrer')
  }

  const handleFormSubmitted = () => {
    onFormSubmitted?.()
    onClose()
  }

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-attend-modal-title"
        className={`bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-h-[92vh] flex flex-col overflow-hidden ${
          isPsychological || isNotesForm ? 'max-w-3xl' : 'max-w-lg'
        }`}
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <Heading
              as="h2"
              size="2xl"
              id="appointment-attend-modal-title"
              className="text-gray-900 dark:text-white"
            >
              Formulário do atendimento
            </Heading>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-6 flex-1 min-h-0">
          <div className="space-y-3">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              Dados gerais
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
              <GeneralField label="Segurado" value={appointment.holder_people?.name} />
              <GeneralField
                label="Data de nascimento"
                value={formatDateOnlyPtBr(appointment.holder_people?.captured_birth_date)}
              />
              <GeneralField label="CPF" value={formatCpf(appointment.holder_cpf)} />
              <GeneralField label="Paciente" value={appointment.patient_people?.name} />
              <GeneralField label="CPF do paciente" value={formatCpf(appointment.patient_cpf)} />
              <GeneralField label="Grau de parentesco" value={patientKinshipLabel(appointment)} />
              <GeneralField label="Assistência" value={appointment.assistance?.name} />
            </div>
          </div>

          <AppointmentAttachmentsList
            appointmentId={appointment.id}
            variant="section"
            title="Anexos"
            canUpload={canEditForm}
          />

          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
            <div>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Formulário específico
                </Heading>
                {formLabel ? (
                  <Text size="sm" color="muted">
                    {formLabel}
                  </Text>
                ) : null}
              </div>
            </div>

            {!formKind ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                {ASSISTANCE_FORM_KIND_MISSING}
              </div>
            ) : isPsychological ? (
              triageOk ? (
                <PsychologicalAttendForm
                  appointment={appointment}
                  canSubmit={canEditForm}
                  onClose={onClose}
                  onSubmitted={handleFormSubmitted}
                  onDraftSaved={onDraftSaved}
                  actionsHost={actionsHost}
                />
              ) : (
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                  Triagem pendente ou ausente; formulário não permitido. Conclua a triagem antes.
                </div>
              )
            ) : isNotesForm ? (
              triageOk ? (
                <SimpleAttendanceNotesForm
                  appointment={appointment}
                  formKind={formKind}
                  canSubmit={canEditForm}
                  label={notesLabel}
                  onClose={onClose}
                  onSubmitted={handleFormSubmitted}
                  onDraftSaved={onDraftSaved}
                  actionsHost={actionsHost}
                />
              ) : (
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                  Triagem pendente ou ausente; formulário não permitido. Conclua a triagem antes.
                </div>
              )
            ) : (
              <div className="space-y-4">
                {!triageOk && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                    Triagem pendente ou ausente; formulário não permitido.
                  </div>
                )}
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                  Em breve: formulário específico por profissional
                  {formLabel ? ` (${formLabel})` : ''}. Use Finalizar atendimento na agenda para
                  marcar o agendamento como realizado.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-gray-900">
          <Button
            variant="outline"
            onClick={openMeetingLink}
            disabled={!meetingLink}
            title={
              meetingLink
                ? 'Abrir o link da reunião em uma nova aba'
                : 'Link da reunião ainda não disponível.'
            }
          >
            <VideoCameraIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            Acessar reunião
          </Button>
          <div ref={setActionsHost} className="flex flex-wrap justify-end gap-3">
            {isFallbackForm && (
              <Button variant="ghost" onClick={onClose}>
                Fechar
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
